import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, MapPin, ChevronDown, BadgeCheck } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { content, lang, t, ui } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg1 = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const yImg2 = useTransform(scrollYProgress, [0, 1], [0, -110]);

  const hero = content.hero;
  const wa = `https://wa.me/${content.whatsapp}?text=${encodeURIComponent(ui.whatsappMsg)}`;
  const imgs = hero.images || [];

  return (
    <section ref={ref} className="hero-grid relative min-h-screen overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-20 h-[480px] w-[480px] rounded-full bg-volt/10 blur-[140px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-blue-500/10 blur-[140px]" />

      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 pb-28 pt-36 lg:grid-cols-2 lg:pt-40">
        <div key={lang}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt"
            data-testid="hero-kicker"
          >
            {t(hero.kicker)}
          </motion.p>

          <h1 className="mt-5 font-display text-6xl uppercase leading-[0.92] tracking-tight sm:text-7xl lg:text-8xl" data-testid="hero-headline">
            {hero.lines.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.13, duration: 0.85, ease: EASE }}
                  className={`block ${line.accent ? "text-volt" : "text-white"}`}
                >
                  {t(line)}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: EASE }}
            className="mt-6 max-w-lg border-l-2 border-volt pl-4 text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subtitle"
          >
            {t(hero.subtitle)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-call-cta"
              href={`tel:${content.phone_href}`}
              className="group flex items-center gap-3 rounded-full bg-volt px-7 py-4 text-sm font-extrabold uppercase tracking-wide text-navy transition-all hover:-translate-y-0.5 hover:bg-volt-hover hover:shadow-[0_10px_40px_rgba(255,212,0,0.35)]"
            >
              <Phone size={18} className="transition-transform group-hover:rotate-12" />
              {ui.call} · {content.phone}
            </a>
            <a
              data-testid="hero-whatsapp-cta"
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-full border-2 border-[#25D366] px-7 py-4 text-sm font-extrabold uppercase tracking-wide text-[#25D366] transition-all hover:-translate-y-0.5 hover:bg-[#25D366] hover:text-navy"
            >
              <MessageCircle size={18} /> {ui.whatsapp}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium text-slate-400"
          >
            <span className="flex items-center gap-1.5"><MapPin size={14} className="text-volt" /> Providence · Warwick · Pawtucket</span>
            <span className="flex items-center gap-1.5"><BadgeCheck size={14} className="text-volt" /> {lang === "es" ? "Personal confiable" : "Trusted staff"}</span>
          </motion.div>
        </div>

        <div className="relative hidden h-[620px] lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 70, damping: 14, delay: 0.7 }}
            className="absolute right-2 top-0 z-20"
          >
            <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}>
              <img
                data-testid="hero-logo"
                src={content.logo}
                alt="D'Best Cleaning Services LLC"
                className="logo-glow w-60 rounded-2xl border-2 border-volt/70 xl:w-72"
              />
            </motion.div>
          </motion.div>

          {imgs[0] && (
            <motion.div style={{ y: yImg1 }} className="absolute left-0 top-28 w-[55%]">
              <motion.img
                initial={{ opacity: 0, y: 70 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.9, ease: EASE }}
                src={imgs[0]}
                alt="Limpieza comercial"
                className="h-[420px] w-full rounded-3xl border border-white/10 object-cover"
                data-testid="hero-image-1"
              />
            </motion.div>
          )}
          {imgs[1] && (
            <motion.div style={{ y: yImg2 }} className="absolute bottom-0 right-0 w-[48%]">
              <motion.img
                initial={{ opacity: 0, y: 70 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.9, ease: EASE }}
                src={imgs[1]}
                alt="Oficina limpia"
                className="h-[300px] w-full rounded-3xl border border-volt/30 object-cover"
                data-testid="hero-image-2"
              />
            </motion.div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, type: "spring", stiffness: 80 }}
          className="flex justify-center lg:hidden"
        >
          <img src={content.logo} alt="D'Best Cleaning Services LLC" className="logo-glow w-52 rounded-2xl border-2 border-volt/70" data-testid="hero-logo-mobile" />
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-slate-500 lg:block"
      >
        <ChevronDown size={22} />
      </motion.div>
    </section>
  );
}
